/**
 * The persistent shell: a collapsible navy rail, and the page.
 *
 * The rail is chrome, not content, and it now looks like it: a deep
 * blue-charcoal ground (`--nav-bg`) visibly distinct from the near-black page
 * canvas, identical on every route and in both themes. Every ink value on it is
 * measured against that ground — labels 10.9:1, icons 6.6:1, active label
 * 13.4:1 — so nothing on the rail hovers near the AA line.
 *
 * Three states, all deliberate:
 *
 *   expanded   icons + labels, 14rem. The active item is a filled pill AND a
 *              left edge bar — two channels, so the state survives squinting.
 *   collapsed  icons only, 3.25rem. The edge bar stays; hovering or FOCUSING
 *              an icon shows a tooltip with the module name, because an icon
 *              alone is not a name. The preference persists per browser.
 *   drawer     under 66rem the rail overlays the content, opened from the top
 *              bar, closed by Escape, scrim, or navigating.
 *
 * The account section lives at the rail's foot in its own darker well —
 * identity and authority visible on every screen, including `via_bootstrap`,
 * which is a temporary state meant to be seen until someone removes it.
 */

import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';

import { useAuth } from '../lib/auth';
import type { MeResponse } from '../types/api';
import { Badge } from './Badge';
import { Button } from './ui/Button';
import {
  BuildingIcon,
  CatalogIcon,
  ChartIcon,
  ChevronRightIcon,
  ChipIcon,
  CompareIcon,
  DashboardIcon,
  LogoutIcon,
  MenuIcon,
  ServerDnsIcon,
  StethoscopeIcon,
  TerminalIcon,
  UserAdminIcon,
  WarningIcon,
  type IconProps,
} from './ui/Icon';
import { Tooltip } from './ui/Tooltip';

interface NavEntry {
  to: string;
  label: string;
  icon: (props: IconProps) => JSX.Element;
  end: boolean;
}

interface NavGroup {
  label: string;
  items: readonly NavEntry[];
}

/**
 * Ten flat entries asked the reader to hold the whole console in their head.
 * Grouped by the question being asked rather than by the object being acted on:
 * *what is the state of things*, *what exists*, *what am I changing*, *what is
 * wrong right now*, *who may touch it*.
 *
 * The order is deliberate — Operate sits below Change because you reach for
 * Issues and Logs after an apply, and Admin is last because it is the rarest.
 */
const NAV_GROUPS: readonly NavGroup[] = [
  {
    label: 'Fleet',
    items: [
      { to: '/', label: 'Overview', icon: DashboardIcon, end: true },
      { to: '/reports', label: 'Reports', icon: ChartIcon, end: false },
    ],
  },
  {
    label: 'Estate',
    items: [
      { to: '/centers', label: 'Centers', icon: BuildingIcon, end: false },
      { to: '/clinics', label: 'Clinics', icon: StethoscopeIcon, end: false },
      { to: '/edge-nodes', label: 'Edge Nodes', icon: ChipIcon, end: false },
    ],
  },
  {
    label: 'Change',
    items: [
      { to: '/plan', label: 'Plan', icon: CompareIcon, end: false },
      { to: '/dns', label: 'DNS', icon: ServerDnsIcon, end: false },
    ],
  },
  {
    label: 'Operate',
    items: [
      { to: '/issues', label: 'Issues', icon: WarningIcon, end: false },
      { to: '/logs', label: 'Logs', icon: CatalogIcon, end: false },
      // Owner-only on the server; shown to everyone so a non-owner learns the
      // record exists (the page's 403 says who can open it).
      { to: '/terminal-sessions', label: 'Terminal sessions', icon: TerminalIcon, end: false },
    ],
  },
  {
    label: 'Admin',
    items: [{ to: '/admins', label: 'Operators', icon: UserAdminIcon, end: false }],
  },
] as const;

const COLLAPSE_KEY = 'h360.sidebar.collapsed';

/** localStorage can throw (privacy modes); a shell must never die over a preference. */
function readCollapsed(): boolean {
  try {
    return window.localStorage.getItem(COLLAPSE_KEY) === '1';
  } catch {
    return false;
  }
}

function writeCollapsed(value: boolean): void {
  try {
    window.localStorage.setItem(COLLAPSE_KEY, value ? '1' : '0');
  } catch {
    /* the preference simply does not persist */
  }
}

const ROLE_TONE = { owner: 'info', operator: 'info', viewer: 'neutral' } as const;

export function Layout({ me }: { me: MeResponse }) {
  const auth = useAuth();
  const { pathname } = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(readCollapsed);

  useEffect(() => setDrawerOpen(false), [pathname]);

  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setDrawerOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [drawerOpen]);

  function toggleCollapsed() {
    setCollapsed((value) => {
      writeCollapsed(!value);
      return !value;
    });
  }

  const roleTone = ROLE_TONE[me.role as keyof typeof ROLE_TONE] ?? 'neutral';

  const link = (item: NavEntry) => {
    const Icon = item.icon;
    const anchor = (
      <NavLink
        to={item.to}
        end={item.end}
        className={({ isActive }) => `rail__link${isActive ? ' rail__link--active' : ''}`}
      >
        <Icon size={17} className="rail__icon" />
        <span className="rail__label">{item.label}</span>
      </NavLink>
    );
    // A collapsed icon is the entire control; the tooltip is its name, shown on
    // hover AND keyboard focus. Expanded, the label is right there — no bubble.
    return collapsed ? (
      <Tooltip key={item.to} label={item.label} side="right">
        {anchor}
      </Tooltip>
    ) : (
      <span key={item.to}>{anchor}</span>
    );
  };

  return (
    <div
      className={[
        'shell',
        drawerOpen ? 'shell--drawer-open' : '',
        collapsed ? 'shell--collapsed' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <a className="skip-link" href="#main">
        Skip to content
      </a>

      <div className="shell__scrim" onClick={() => setDrawerOpen(false)} aria-hidden="true" />

      <nav className="rail" aria-label="Sections">
        <div className="rail__brand">
          {/* alt="" on purpose: the brand text beside this carries the
              accessible name, and in the collapsed rail the mark is decoration
              for a nav that is already labelled. */}
          <span className="rail__mark" aria-hidden="true">
            <img src="/pwa-192x192.png" alt="" width={26} height={26} />
          </span>
          <span className="rail__brand-text">
            <span className="rail__brand-org">Health-360</span>
            <span className="rail__brand-app">Fleet Controller</span>
          </span>
        </div>

        {/* One <ul> per group, labelled, so the grouping is announced rather
            than being a purely visual arrangement. Collapsed, the headings are
            hidden and the border between groups carries the same information
            in the only way 3.25rem allows — the tooltip still names each icon,
            so nothing is lost, only condensed. */}
        <div className="rail__list">
          {NAV_GROUPS.map((group) => (
            <div className="rail__group" key={group.label}>
              <p className="rail__group-label" aria-hidden="true">
                {group.label}
              </p>
              <ul className="rail__group-items" aria-label={group.label}>
                {group.items.map((item) => (
                  <li key={item.to}>{link(item)}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* ── account ─────────────────────────────────────────────────── */}
        <div className="rail__foot">
          <div className="rail__identity" title={me.email}>
            <span className="rail__avatar" aria-hidden="true">
              {me.email.slice(0, 1).toUpperCase()}
            </span>
            <span className="rail__identity-text">
              <span className="rail__email">{me.email}</span>
              <span className="rail__tags">
                <Badge tone={roleTone} small>
                  {me.role}
                </Badge>
                {me.via_bootstrap ? (
                  <Badge
                    tone="warning"
                    small
                    title="Authority comes from FLEET_BOOTSTRAP_ADMIN_EMAILS rather than a platform_admins row. Add a database row and clear the env var."
                  >
                    bootstrap
                  </Badge>
                ) : null}
                {auth.mode === 'none' ? (
                  <Badge
                    tone="neutral"
                    small
                    title="VITE_AUTH0_DOMAIN is unset, so this build sends no bearer token. The controller must be running with dev_no_auth on a loopback bind."
                  >
                    auth off
                  </Badge>
                ) : null}
              </span>
            </span>
            {auth.mode !== 'none' ? (
              <Tooltip label="Sign out" side="top">
                <button type="button" className="rail__signout" onClick={auth.logout}>
                  <LogoutIcon size={15} />
                  <span className="visually-hidden">Sign out</span>
                </button>
              </Tooltip>
            ) : null}
          </div>

          {/* Collapse toggle. Desktop only — the drawer has its own affordance. */}
          <Tooltip label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'} side="right">
            <button
              type="button"
              className="rail__collapse"
              aria-expanded={!collapsed}
              onClick={toggleCollapsed}
            >
              <ChevronRightIcon size={14} className="rail__collapse-chevron" />
              <span className="rail__label">Collapse</span>
            </button>
          </Tooltip>
        </div>
      </nav>

      <div className="shell__main">
        <div className="shell__topbar">
          <Button
            kind="ghost"
            size="sm"
            hasIconOnly
            renderIcon={MenuIcon}
            iconDescription={drawerOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={drawerOpen}
            onClick={() => setDrawerOpen((open) => !open)}
          />
          <span className="shell__topbar-title">Fleet Controller</span>
        </div>

        <main id="main" className="shell__content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
